import type { AppelApi } from '../../../api/client.js';
import { ErreurReseau, estErreurReseau } from '../../../api/errors.js';
import {
  presentationApi,
  type ChargePresentation,
  type InteretPresentation,
} from '../../../api/presentation.js';
import type { SourcePresentation } from '../../../api/sourcePresentation.js';
import { capturerPortee, exigerActuelle } from '../../../auth/identite.js';
import {
  empilerActionFoire,
  lireFileHorsLigne,
  type PorteeFile,
} from '../../stand/fileHorsLigne.js';
import { cleElementPresentation } from '../elementsPresentation.js';
import { magasinsPresentation } from './index.js';
import type { MagasinPresentation } from './magasin.js';

export interface CorpsInteretPresentation {
  selectionId: string;
  oeuvreId: string;
  exemplaireId: string | null;
  interesse: boolean;
}
export function interetsEnAttente(portee: PorteeFile, selectionId: string): InteretPresentation[] {
  return lireFileHorsLigne(portee)
    .filter(
      (action) =>
        action.type === 'interet-presentation' &&
        (action.corps as CorpsInteretPresentation).selectionId === selectionId,
    )
    .map((action) => ({ ...(action.corps as CorpsInteretPresentation), marqueLe: action.creeeLe }));
}
function avecInterets(charge: ChargePresentation, portee: PorteeFile): ChargePresentation {
  const interets = new Map(
    charge.interets.map((interet) => [cleElementPresentation(interet), interet]),
  );
  for (const interet of interetsEnAttente(portee, charge.selection.id))
    interets.set(cleElementPresentation(interet), interet);
  return { ...charge, interets: [...interets.values()] };
}
export function creerSourcePresentation(
  portee: PorteeFile,
  magasins: () => MagasinPresentation[] = magasinsPresentation,
): SourcePresentation & { liberer(): void } {
  let sansReseau = !navigator.onLine;
  let chargeCourante: ChargePresentation | null = null;
  const urls = new Map<string, string>();
  function libererImages() {
    for (const url of urls.values()) URL.revokeObjectURL(url);
    urls.clear();
  }
  async function horsLigne(id: string, appel: AppelApi): Promise<ChargePresentation> {
    const capture = capturerPortee();
    for (const magasin of magasins()) {
      const preparation = await magasin.lire(portee, id);
      exigerActuelle(capture);
      appel.signal?.throwIfAborted();
      if (!preparation) continue;
      sansReseau = true;
      libererImages();
      const elements = preparation.charge.elements.map((element) => {
        if (!element.imageUrl) return element;
        const image = preparation.images.get(element.imageUrl);
        if (!image) throw new Error('Image hors ligne absente.');
        let url = urls.get(element.imageUrl);
        if (!url) {
          url = URL.createObjectURL(image);
          urls.set(element.imageUrl, url);
        }
        return { ...element, imageUrl: url };
      });
      chargeCourante = avecInterets({ ...preparation.charge, elements }, portee);
      return chargeCourante;
    }
    throw new ErreurReseau();
  }
  return {
    get horsLigne() {
      return sansReseau || !navigator.onLine;
    },
    async charger(galerie, id, appel) {
      if (!navigator.onLine) return horsLigne(id, appel);
      try {
        const charge = await presentationApi.presenter(galerie, id, appel);
        sansReseau = false;
        chargeCourante = avecInterets(charge, portee);
        return chargeCourante;
      } catch (erreur) {
        if (!estErreurReseau(erreur)) throw erreur;
        return horsLigne(id, appel);
      }
    },
    async marquerInteret(galerie, id, oeuvreId, corps, appel) {
      if (!sansReseau && navigator.onLine) {
        try {
          return await presentationApi.marquerInteret(galerie, id, oeuvreId, corps, appel);
        } catch (erreur) {
          if (!estErreurReseau(erreur)) throw erreur;
        }
      }
      sansReseau = true;
      const action = empilerActionFoire(
        'interet-presentation',
        {
          selectionId: id,
          oeuvreId,
          exemplaireId: corps.exemplaireId ?? null,
          interesse: corps.interesse,
        },
        portee,
      );
      return {
        interet: {
          oeuvreId,
          exemplaireId: corps.exemplaireId ?? null,
          interesse: corps.interesse,
          marqueLe: action.creeeLe,
        },
      };
    },
    async recapitulatif(galerie, id, appel) {
      if (!sansReseau && navigator.onLine) {
        try {
          return await presentationApi.recapitulatif(galerie, id, appel);
        } catch (erreur) {
          if (!estErreurReseau(erreur)) throw erreur;
        }
      }
      const charge = avecInterets(chargeCourante ?? (await horsLigne(id, appel)), portee);
      return {
        selection: charge.selection,
        interets: charge.interets,
        actions: { notes: false, offre: false, affaire: false, reservation: false },
      };
    },
    noter: presentationApi.noter,
    liberer() {
      libererImages();
    },
  };
}
